import React from "react";
import styled from "styled-components";
import Banner from "../components/Banner";
import Header from "../components/Header";
import Wrapper from "../components/Layout/Wrapper";
import useClasses from "../hooks/useClasses";
import useStudent from "../hooks/useStudent";
import firebase from "firebase";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { Activity, Session, Submission } from "../types";
import useAuth from "../hooks/useAuth";
import {
  getStarsToNext,
  getStarsFromAwards,
  levelByStars,
  getLevelFromStars,
} from "../components/LevelBadge/utils";
import LevelBadge from "../components/LevelBadge";

const HomeWrapper = styled.div`
  header {
    margin-bottom: 16px;
  }
  .banner {
    margin-bottom: 16px;
  }
  .progress {
    display: flex;
    align-items: center;
    margin-bottom: 64px;
    margin-top: 16px;
    > div {
      width: 50%;
      .banner {
        width: 100%;
      }
    }

    .bar {
      margin-left: 32px;
      display: flex;
      align-items: center;
      .bar--outer {
        background: #346bbd;
        border: 2px solid #4784df;
        box-sizing: border-box;
        border-radius: 11px;
        margin-left: -32px;
        margin-right: -32px;
        width: 100%;
        height: 32px;
        display: flex;
        align-items: center;
        .bar--inner {
          height: 78%;
          background: #ffc700;
          box-shadow: inset 0px -1px 2px rgba(0, 0, 0, 0.1);
          border-radius: 11px;
        }
      }
    }
    .level-badge-icon-cont {
      width: 128px;
      z-index: 99;
      img {
        width: 128px;
      }
    }
    .locked-icon .level-badge-icon {
      width: 84px;
    }
  }
`;

const Home = () => {
  const { currentUser } = firebase.auth();
  const { studentProfile, submissions } = useStudent(currentUser?.uid);
  const { data: classData } = useClasses();
  const history = useHistory();
  const { accountDetails } = useAuth();
  const studentClasses = classData?.filter((x) =>
    x.students?.some((y) => y.id === studentProfile?.id)
  );
  const studentActivities = (studentClasses || []).reduce(
    (acc, x) => [
      ...acc,
      ...(x.activities || []).map((y) => ({ ...y, classId: x.id })),
    ],
    [] as (Activity & { classId: string })[]
  );
  const studentSessions = (studentClasses || []).reduce(
    (acc, x) => [
      ...acc,
      ...(x.sessions || []).map((y) => ({ ...y, classId: x.id })),
    ],
    [] as (Session & { classId: string })[]
  );

  const activities = studentActivities.filter((x) => x?.isVisible);

  const sessions = studentSessions.filter((x) => x?.isVisible);
  const upcomingSession = sessions
    ?.filter(
      (session) => session?.finishTime && session.finishTime > Date.now()
    )
    .sort((a, b) => (a?.finishTime || 0) - (b?.finishTime || 0))[0];

  const currStars = getStarsFromAwards(accountDetails?.awards || []);

  const getSubtitle = (activity: Activity, submission?: Submission) => {
    if (submission?.marked && (submission?.stars || 0) > activity.stars) {
      return `Amazing! You got ${activity.stars} PLUS an extra ${
        (submission?.stars || 0) - activity.stars
      } stars!`;
    }
    if (submission?.marked) {
      return `Well done! You got ${submission.stars}!`;
    }
    if (submission) {
      return `Good job! A teacher will mark your work soon.`;
    }
    return `Do it and get ${activity.stars}`;
  };

  return (
    <HomeWrapper>
      <Header />
      <Wrapper style={{ paddingTop: 16, flexDirection: "column" }}>
        <div className="progress">
          <div>
            <Banner
              style={{ position: "relative", top: 12 }}
              title={`You have ${currStars} stars`}
              subtitle={`Another ${getStarsToNext(
                currStars
              )} to the next level!`}
              stars={currStars}
            />
          </div>
          <div className="bar">
            <div className="level-badge-icon-cont">
              <LevelBadge stars={currStars} />
            </div>
            <div className="bar--outer">
              <div
                className="bar--inner"
                style={{
                  width: `${
                    (currStars /
                      levelByStars[getLevelFromStars(currStars) + 1]) *
                    100
                  }%`,
                }}
              />
            </div>
            <div className="locked-icon">
              <LevelBadge level={8} />
            </div>
          </div>
        </div>
        {upcomingSession ? (
          <Banner
            title={`${upcomingSession?.name}`}
            subtitle={`${moment(upcomingSession?.startTime).format("LLLL")}`}
            buttonText="View session"
            onClick={() =>
              history.push(
                `/class/${upcomingSession.classId}/session/${upcomingSession?.id}`
              )
            }
            stars={upcomingSession?.stars}
          />
        ) : (
          <h4>You have no upcoming sessions!</h4>
        )}
        <h3>Activities</h3>
        {activities?.length === 0 && <h4>You're all set!</h4>}
        {(activities || [])
          .sort((a, b) => b.finishTime - a.finishTime)
          .map((activity) => {
            const submission = submissions?.find(
              (sub) => sub.activityId === activity.id
            );
            return (
              <Banner
                title={`${activity?.name}`}
                subtitle={getSubtitle(activity, submission)}
                checked={submission?.marked}
                inProgress={!!submission && !submission.marked}
                buttonText="View activity"
                onClick={() =>
                  history.push(
                    `/class/${activity.classId}/activity/${activity?.id}`
                  )
                }
                stars={submission?.stars || activity?.stars}
              />
            );
          })}
      </Wrapper>
    </HomeWrapper>
  );
};

export default Home;
